import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
    ClipboardCheck,
    CheckCircle2,
    AlertTriangle,
    Loader2,
    Upload,
    Trash2,
    FileText,
    ChevronDown,
    ChevronUp,
    Calendar,
    Sparkles,
    ArrowRight,
    Receipt,
    Wallet,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { api, fmtMoney, fmtDate } from '../lib/api';

/**
 * VendorReconciliationPanel (Sprint 2026-01 v2)
 * Upload a vendor ledger PDF/image → OCR (Claude Sonnet 5) →
 * both:
 *   • Top-line reconciliation: our outstanding vs ledger closing balance
 *   • Row-level analysis: which of your bills the vendor missed logging,
 *     which of the vendor's invoices you missed scanning, opening-balance
 *     vs overpayment disambiguation for the payment delta.
 */
export function VendorReconciliationPanel({ vendorKey, vendorName }) {
    const [rec, setRec] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showLineDetails, setShowLineDetails] = useState(true);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        if (!vendorKey) return;
        setLoading(true);
        try {
            const data = await api.getVendorReconciliation(vendorKey);
            setRec(data.exists ? data : null);
        } catch (e) {
            /* ignore */
        } finally {
            setLoading(false);
        }
    }, [vendorKey]);

    useEffect(() => {
        load();
    }, [load]);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const data = await api.reconcileVendor(vendorKey, f);
            setRec(data);
            const st = data.status;
            if (st === 'match') {
                toast.success('Perfect match — books tally!');
            } else {
                toast.warning(data.message || 'Reconciliation completed with a diff');
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to reconcile ledger');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const resolveDelta = async (choice) => {
        try {
            const data = await api.resolvePaymentDelta(vendorKey, choice);
            setRec(data);
            toast.success(
                choice === 'opening_balance'
                    ? 'Marked ₹ delta as opening balance carried forward.'
                    : choice === 'overpayment'
                    ? 'Marked ₹ delta as overpayment.'
                    : 'Marked as unresolved.'
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save your choice');
        }
    };

    const removeReconciliation = async () => {
        if (!vendorKey) return;
        try {
            await api.deleteVendorReconciliation(vendorKey);
            setRec(null);
            toast.success('Reconciliation cleared');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to clear');
        }
    };

    if (loading) {
        return (
            <Card className="p-5">
                <Skeleton className="h-6 w-40 mb-3" />
                <Skeleton className="h-16 w-full" />
            </Card>
        );
    }

    const status = rec?.status;
    const isMatch = status === 'match';
    const isMissing = status === 'missing_bills_or_payments';
    const isExtra = status === 'extra_bills_or_overpayment';

    return (
        <Card className="p-5" data-testid="vendor-reconciliation-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center">
                        <ClipboardCheck className="text-primary" size={18} />
                    </div>
                    <div>
                        <div className="font-heading text-lg">Ledger Reconciliation</div>
                        <div className="text-xs text-muted-foreground">
                            Upload the vendor's account statement — we'll compare their closing balance
                            with your Ease My Bill records.
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={onFile}
                        className="hidden"
                        data-testid="reconcile-file-input"
                    />
                    <Button
                        size="sm"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="gap-2"
                        data-testid="reconcile-upload-btn"
                    >
                        {uploading ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Upload size={14} />
                        )}
                        {rec ? 'Upload new ledger' : 'Upload ledger PDF'}
                    </Button>
                    {/* Sprint 2026-08: Claude Sonnet 5 is the sole OCR engine. */}
                    <div
                        data-testid="reconcile-model-badge"
                        className="text-[10px] uppercase tracking-wide inline-flex items-center gap-1.5 rounded-md border px-2 py-1 border-primary/40 bg-primary/10 text-primary"
                        title="Vendor ledgers are read by our AI — a unified high-accuracy OCR engine."
                    >
                        <Sparkles size={11} />
                        AI
                    </div>
                </div>
            </div>

            {!rec && !uploading && (
                <div
                    className="rounded-md border border-dashed border-border/70 bg-muted/30 p-6 text-center text-sm text-muted-foreground"
                    data-testid="reconcile-empty"
                >
                    No ledger uploaded yet for {vendorName || 'this vendor'}. Ask your vendor for their
                    account statement (they'll usually export it from Tally / Zoho / Marg as a PDF) and
                    upload it here to check if books tally.
                </div>
            )}

            {rec && (
                <div className="space-y-3" data-testid="reconcile-result">
                    {/* Status headline */}
                    <div
                        className={
                            'rounded-md border p-3 flex items-start gap-3 ' +
                            (isMatch
                                ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950'
                                : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950')
                        }
                    >
                        {isMatch ? (
                            <CheckCircle2 className="text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" size={18} />
                        ) : (
                            <AlertTriangle className="text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" size={18} />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className={
                                'font-medium ' + (isMatch ? 'text-emerald-900 dark:text-emerald-100' : 'text-amber-900 dark:text-amber-100')
                            }
                                data-testid="reconcile-status-message"
                            >
                                {rec.message}
                            </div>
                            {rec.hint && (
                                <div className={
                                    'text-sm mt-1 ' + (isMatch ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300')
                                }>
                                    {rec.hint}
                                </div>
                            )}
                            {isMissing && (
                                <div className="text-sm mt-2 text-amber-900 dark:text-amber-100">
                                    <span className="font-medium">Action:</span> upload the missing
                                    bill(s) via the app or WhatsApp. As you add them, this diff will
                                    automatically shrink. Refresh this page after uploading to re-check.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Numbers grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <MetricTile
                            label="Vendor's Ledger"
                            value={fmtMoney(rec.ledger_outstanding ?? rec.closing_balance, rec.currency)}
                            sub={rec.closing_direction === 'we_owe' ? 'We owe' : rec.closing_direction === 'vendor_owes' ? 'Vendor owes us' : 'Settled'}
                            testid="reconcile-tile-ledger"
                        />
                        <MetricTile
                            label="Our Records"
                            value={fmtMoney(rec.outstanding_by_us, rec.currency)}
                            sub={`${rec.bill_count || 0} bill${rec.bill_count === 1 ? '' : 's'} \u00b7 ${rec.payment_count || 0} payment${rec.payment_count === 1 ? '' : 's'}`}
                            testid="reconcile-tile-ours"
                        />
                        <MetricTile
                            label="Difference"
                            value={fmtMoney(Math.abs(rec.diff || 0), rec.currency)}
                            sub={
                                Math.abs(rec.diff || 0) < 0.5
                                    ? 'Books tally'
                                    : rec.diff > 0
                                    ? 'Ledger higher'
                                    : 'Our records higher'
                            }
                            highlight={!isMatch}
                            testid="reconcile-tile-diff"
                        />
                    </div>

                    {/* 2026-07: prominent "missing invoices" callout — surfaces the
                        most actionable row-level info right at the top of the panel so
                        users don't have to scroll to find the specific bills to chase. */}
                    <MissingInvoicesCallout analysis={rec.line_analysis} currency={rec.currency} />

                    {/* Sprint 2026-01 v2: row-level analysis */}
                    {rec.line_analysis && (
                        <LineAnalysisSection
                            analysis={rec.line_analysis}
                            resolution={rec.payment_delta_resolution}
                            onResolve={resolveDelta}
                            currency={rec.currency}
                            show={showLineDetails}
                            onToggle={() => setShowLineDetails((v) => !v)}
                        />
                    )}

                    {/* Details */}
                    <div>
                        <button
                            type="button"
                            className="text-xs text-muted-foreground inline-flex items-center gap-1 underline decoration-dotted"
                            onClick={() => setShowDetails((v) => !v)}
                            data-testid="reconcile-toggle-details"
                        >
                            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {showDetails ? 'Hide details' : 'Show details'}
                        </button>
                        {showDetails && (
                            <div className="mt-2 rounded-md border border-border/60 bg-muted/30 p-3 text-xs space-y-1">
                                <DetailRow label="Statement issued by" value={rec.vendor_name_from_ledger || vendorName} />
                                <DetailRow label="Statement addressed to" value={rec.customer_name_from_ledger} />
                                <DetailRow
                                    label="Period"
                                    value={
                                        rec.period_from || rec.period_to
                                            ? `${fmtDate(rec.period_from) || '—'}  \u2192  ${fmtDate(rec.period_to) || '—'}`
                                            : '\u2014'
                                    }
                                />
                                <DetailRow label="Ledger opening balance" value={
                                    rec.opening_balance != null
                                        ? `${fmtMoney(rec.opening_balance, rec.currency)} (${rec.opening_direction || '\u2014'})`
                                        : '\u2014'
                                } />
                                <DetailRow label="Ledger total debits" value={rec.total_debits != null ? fmtMoney(rec.total_debits, rec.currency) : '\u2014'} />
                                <DetailRow label="Ledger total credits" value={rec.total_credits != null ? fmtMoney(rec.total_credits, rec.currency) : '\u2014'} />
                                <DetailRow label="Ledger line count" value={rec.line_count} />
                                <DetailRow label="Our billed total" value={fmtMoney(rec.billed_by_us, rec.currency)} />
                                <DetailRow label="Our paid total" value={fmtMoney(rec.paid_by_us, rec.currency)} />
                                <DetailRow label="File" value={rec.original_filename} />
                                <DetailRow label="Uploaded" value={rec.uploaded_at ? new Date(rec.uploaded_at).toLocaleString() : '\u2014'} />
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeReconciliation}
                            className="gap-1 text-muted-foreground hover:text-foreground"
                            data-testid="reconcile-delete-btn"
                        >
                            <Trash2 size={12} /> Clear this reconciliation
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function MissingInvoicesCallout({ analysis, currency }) {
    // 2026-07: hoist the most actionable row-level info (specific invoice
    // numbers on the vendor's ledger that we haven't scanned yet) to the top
    // of the panel — right below the amount tiles. Silent when there's
    // nothing to chase.
    if (!analysis) return null;
    const missing = analysis?.unmatched_in_ledger?.invoices || [];
    if (missing.length === 0) return null;

    const total = missing.reduce((s, x) => s + Number(x.amount || 0), 0);
    const preview = missing.slice(0, 5);
    const rest = Math.max(0, missing.length - preview.length);

    return (
        <div
            className="rounded-lg border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 p-4"
            data-testid="reconcile-missing-callout"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle
                    className="text-amber-700 dark:text-amber-400 shrink-0 mt-0.5"
                    size={20}
                />
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-amber-900 dark:text-amber-100">
                        {missing.length === 1
                            ? '1 bill on the vendor\u2019s ledger is not in your app yet'
                            : `${missing.length} bills on the vendor\u2019s ledger are not in your app yet`}
                        {total > 0 && (
                            <>
                                {' \u2014 total '}
                                <span className="font-mono tabular-nums">{fmtMoney(total, currency)}</span>
                            </>
                        )}
                    </div>
                    <div className="text-xs text-amber-900/80 dark:text-amber-200/70 mt-0.5">
                        Please scan and upload these to close the reconciliation gap.
                    </div>

                    <ul className="mt-3 space-y-1.5" data-testid="reconcile-missing-list">
                        {preview.map((x, idx) => (
                            <li
                                key={`${x.invoice_number || 'inv'}-${idx}`}
                                className="flex items-center gap-3 text-sm rounded-md bg-white/70 dark:bg-black/20 border border-amber-200/70 dark:border-amber-900/40 px-3 py-1.5"
                                data-testid={`reconcile-missing-item-${idx}`}
                            >
                                <Badge
                                    variant="outline"
                                    className="font-mono tabular-nums text-xs shrink-0 border-amber-300 dark:border-amber-800 bg-white dark:bg-black/40"
                                    data-testid={`reconcile-missing-invoice-${idx}`}
                                >
                                    {x.invoice_number || 'No number'}
                                </Badge>
                                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                                    {fmtDate(x.date) || '\u2014'}
                                </span>
                                {x.particulars && (
                                    <span
                                        className="text-xs text-muted-foreground truncate flex-1 min-w-0"
                                        title={x.particulars}
                                    >
                                        {x.particulars}
                                    </span>
                                )}
                                <span className="text-sm font-mono tabular-nums ml-auto shrink-0">
                                    {fmtMoney(x.amount, currency)}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {rest > 0 && (
                        <div
                            className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/70"
                            data-testid="reconcile-missing-more"
                        >
                            +{rest} more \u2014 expand \u201cRow-by-row analysis\u201d below to see all.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function MetricTile({ label, value, sub, highlight, testid }) {
    return (
        <div
            className={
                'rounded-md border p-3 ' +
                (highlight
                    ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950'
                    : 'border-border/60 bg-background/60')
            }
            data-testid={testid}
        >
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="font-mono text-lg tabular-nums mt-1">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-mono text-right">{value == null || value === '' ? '\u2014' : value}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sprint 2026-01 v2 — row-level line analysis section
// ---------------------------------------------------------------------------
function VerdictBanner({ verdict, tone = 'warning', testid }) {
    if (!verdict) return null;
    const sev = verdict.severity || tone;
    const ok = sev === 'ok';
    const cls = ok
        ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-100'
        : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100';
    const Icon = ok ? CheckCircle2 : AlertTriangle;
    return (
        <div className={`rounded-md border p-3 flex items-start gap-3 ${cls}`} data-testid={testid}>
            <Icon size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">{verdict.message}</div>
        </div>
    );
}

function LineAnalysisSection({ analysis, resolution, onResolve, currency, show, onToggle }) {
    if (!analysis) return null;
    const {
        invoice_verdict: iv,
        payment_verdict: pv,
        unmatched_in_ledger: ul,
        unmatched_in_app: ua,
        matches,
        ledger_summary: ls,
        app_summary: as,
    } = analysis;

    const paymentDelta = pv?.delta ?? 0;
    const showResolution = pv && pv.available_actions && pv.available_actions.length > 0;

    return (
        <div className="space-y-3 pt-1" data-testid="reconcile-line-analysis">
            <Separator />
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-heading text-base flex items-center gap-2">
                        <Receipt size={16} className="text-primary" />
                        Row-by-row analysis
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {ls?.row_count || 0} ledger row(s) · matched {matches?.invoices?.length || 0} invoice(s) and {matches?.payments?.length || 0} payment(s)
                    </div>
                </div>
                <button
                    type="button"
                    className="text-xs text-muted-foreground inline-flex items-center gap-1 underline decoration-dotted"
                    onClick={onToggle}
                    data-testid="reconcile-toggle-line-details"
                >
                    {show ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {show ? 'Hide row detail' : 'Show row detail'}
                </button>
            </div>

            {/* Invoice verdict */}
            <VerdictBanner verdict={iv} testid="reconcile-invoice-verdict" />

            {/* Payment verdict + resolution actions */}
            <VerdictBanner verdict={pv} testid="reconcile-payment-verdict" />
            {showResolution && (
                <div
                    className="rounded-md border border-border/70 bg-muted/30 p-3"
                    data-testid="reconcile-resolution"
                >
                    <div className="text-xs text-muted-foreground mb-2">
                        How should we treat the {fmtMoney(Math.abs(paymentDelta), currency)} payment delta?
                        {resolution && (
                            <Badge variant="outline" className="ml-2 text-[10px]">
                                Current: {resolution.replace(/_/g, ' ')}
                            </Badge>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant={resolution === 'opening_balance' ? 'default' : 'outline'}
                            onClick={() => onResolve('opening_balance')}
                            data-testid="reconcile-resolve-opening"
                            className="gap-1"
                        >
                            <Wallet size={14} /> Mark as opening balance
                        </Button>
                        <Button
                            size="sm"
                            variant={resolution === 'overpayment' ? 'default' : 'outline'}
                            onClick={() => onResolve('overpayment')}
                            data-testid="reconcile-resolve-overpay"
                            className="gap-1"
                        >
                            <ArrowRight size={14} /> Mark as overpayment
                        </Button>
                        {resolution && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onResolve('unresolved')}
                                data-testid="reconcile-resolve-clear"
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {show && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reconcile-row-lists">
                    <MissingList
                        title="On vendor's ledger, missing from your scans"
                        subtitle="You may have forgotten to scan these bills."
                        emptyMsg="Nothing missing — every ledger invoice matches a scanned bill."
                        items={ul?.invoices || []}
                        renderItem={(x) => ({
                            label: `${fmtDate(x.date) || '—'} · ${x.invoice_number || x.particulars || 'Invoice'}`,
                            amount: x.amount,
                        })}
                        currency={currency}
                        tone="warning"
                        testid="reconcile-missing-scans"
                    />
                    <MissingList
                        title="In your scans, missing from vendor's ledger"
                        subtitle="The vendor may have forgotten to log these invoices."
                        emptyMsg="Vendor's ledger includes every bill you've scanned."
                        items={ua?.bills || []}
                        renderItem={(x) => ({
                            label: `${fmtDate(x.bill_date) || '—'} · ${x.invoice_number || 'Bill'}`,
                            amount: x.total,
                        })}
                        currency={currency}
                        tone="info"
                        testid="reconcile-vendor-missed-bills"
                    />
                    <MissingList
                        title="Payments on vendor's ledger, unrecorded here"
                        subtitle="Log these payments in your vendor ledger."
                        emptyMsg="Every ledger payment matches a recorded payment."
                        items={ul?.payments || []}
                        renderItem={(x) => ({
                            label: `${fmtDate(x.date) || '—'} · ${x.reference || x.particulars || 'Payment'}`,
                            amount: x.amount,
                        })}
                        currency={currency}
                        tone="warning"
                        testid="reconcile-missing-app-payments"
                    />
                    <MissingList
                        title="Payments here, missing from vendor's ledger"
                        subtitle="Follow up with the vendor to record these payments."
                        emptyMsg="Vendor's ledger acknowledges every payment you've made."
                        items={ua?.payments || []}
                        renderItem={(x) => ({
                            label: `${fmtDate(x.paid_at) || '—'} · ${x.method || x.reference || 'Payment'}`,
                            amount: x.amount,
                        })}
                        currency={currency}
                        tone="info"
                        testid="reconcile-vendor-missed-payments"
                    />
                </div>
            )}
        </div>
    );
}

function MissingList({ title, subtitle, emptyMsg, items, renderItem, currency, tone, testid }) {
    const has = Array.isArray(items) && items.length > 0;
    const badgeCls =
        tone === 'warning'
            ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800'
            : 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-800';
    return (
        <div
            className="rounded-md border border-border/60 bg-background/60 p-3"
            data-testid={testid}
        >
            <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs font-medium">{title}</div>
                <span className={`text-[10px] uppercase tracking-wide border rounded-full px-1.5 py-0.5 ${badgeCls}`}>
                    {has ? items.length : 'none'}
                </span>
            </div>
            <div className="text-[11px] text-muted-foreground mb-2">{subtitle}</div>
            {has ? (
                <ul className="space-y-1 text-xs max-h-52 overflow-y-auto pr-1">
                    {items.map((x, i) => {
                        const r = renderItem(x);
                        return (
                            <li
                                key={i}
                                className="flex items-center justify-between gap-2 rounded-sm border border-border/40 bg-card/60 px-2 py-1"
                            >
                                <span className="truncate">{r.label}</span>
                                <span className="font-mono tabular-nums">
                                    {r.amount != null ? fmtMoney(r.amount, currency) : '—'}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <div className="text-xs text-muted-foreground italic">{emptyMsg}</div>
            )}
        </div>
    );
}
